import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  AutoComplete,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  DatePicker,
  Tooltip,
  Row,
  Col,
} from "antd";
import moment from "moment";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const UK14_ALLERGEN_OPTIONS = [
  { value: "celery", label: "Celery" },
  { value: "cereals_containing_gluten", label: "Cereals Containing Gluten" },
  { value: "crustaceans", label: "Crustaceans" },
  { value: "eggs", label: "Eggs" },
  { value: "fish", label: "Fish" },
  { value: "lupin", label: "Lupin" },
  { value: "milk", label: "Milk" },
  { value: "molluscs", label: "Molluscs" },
  { value: "mustard", label: "Mustard" },
  { value: "peanuts", label: "Peanuts" },
  { value: "sesame", label: "Sesame" },
  { value: "soybeans", label: "Soybeans" },
  { value: "sulphur_dioxide", label: "Sulphur Dioxide / Sulphites" },
  { value: "tree_nuts", label: "Tree Nuts" },
];

const UK14_ALLERGEN_VALUES = new Set(
  UK14_ALLERGEN_OPTIONS.map((item) => item.value)
);

const normalizeUk14Allergens = (values) => {
  if (!Array.isArray(values)) return [];
  return [...new Set(
    values
      .map((value) => String(value || "").trim().toLowerCase())
      .filter((value) => UK14_ALLERGEN_VALUES.has(value))
  )];
};

const nonNegativeRule = {
  validator: (_, value) => {
    if (value == null || value === "") return Promise.resolve();
    const n = Number(value);
    if (!Number.isFinite(n))
      return Promise.reject(new Error("Enter a valid number"));
    if (n < 0)
      return Promise.reject(new Error("Negative values are not allowed"));
    return Promise.resolve();
  },
};

const roundEnergy = (value) => Number((Number(value) || 0).toFixed(1));
const isBlank = (value) => value === null || value === undefined || value === "";

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const Search = Input.Search;
const withTooltip = (value) => {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <Tooltip title={text}>
      <span>{text}</span>
    </Tooltip>
  );
};

const statusTagColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return "gold";
  if (s === "approved") return "green";
  if (s === "rejected") return "red";
  return "default";
};

const toNumOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const formatNutrient = (v, unit = "g", digits = 1) => {
  const n = toNumOrNull(v);
  if (n == null) return "—";
  return `${n.toFixed(digits)}${unit}`;
};

const CustomIngredientManager = () => {
  const { request } = useRequest();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [filters, setFilters] = useState({
    q: "",
    sourceType: "all",
    dateRange: undefined,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    q: "",
    sourceType: "all",
    dateRange: undefined,
  });

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [savingEdit, setSavingEdit] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [editForm] = Form.useForm();

  const selectedAllergens = Form.useWatch("allergens", editForm);
  const selectedMayContain = Form.useWatch("mayContain", editForm);

  const allergenOptionsForContains = useMemo(
    () => {
      const list = selectedMayContain || [];
      return UK14_ALLERGEN_OPTIONS.map((option) => ({
        ...option,
        disabled: list.includes(option.value),
      }));
    },
    [selectedMayContain],
  );
  const allergenOptionsForMayContain = useMemo(
    () => {
      const list = selectedAllergens || [];
      return UK14_ALLERGEN_OPTIONS.map((option) => ({
        ...option,
        disabled: list.includes(option.value),
      }));
    },
    [selectedAllergens],
  );

  const manualEnergyFieldsRef = useRef({ energyKcal: false, energyKj: false });

  // Category dropdown (searchable + free text)
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);

  const fetchAllCategories = async () => {
    setCategoryLoading(true);
    try {
      await request({
        url: apiPath.authAdminIngredientCategoriesSuggestions,
        method: "GET",
        onSuccess: (data) => {
          const list = Array.isArray(data?.categories) ? data.categories : [];
          const opts = list.map((c) => ({ value: c, label: c }));
          setCategoryOptions(opts);
        },
        onError: () => {},
      });
    } finally {
      setCategoryLoading(false);
    }
  };

  useEffect(() => {
    if (editModalOpen || createModalOpen) {
      fetchAllCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editModalOpen, createModalOpen]);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);
    if (appliedFilters.q?.trim()) params.append("q", appliedFilters.q.trim());
    if (appliedFilters.sourceType && appliedFilters.sourceType !== "all") {
      params.append("sourceType", appliedFilters.sourceType);
    }
    if (appliedFilters.dateRange && appliedFilters.dateRange.length === 2) {
      params.append("from", appliedFilters.dateRange[0].format("YYYY-MM-DD"));
      params.append("to", appliedFilters.dateRange[1].format("YYYY-MM-DD"));
    }
    return params.toString();
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const url = `${apiPath.authAdminCustomIngredientsList}?${buildQueryString()}`;
      await request({
        url,
        method: "GET",
        onSuccess: (data) => {
          const next = data?.items || [];
          setItems(next);
          setTotal(data?.total || 0);
        },
        onError: (err) => {
          const status = err?.response?.status;
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch custom ingredients";
          ShowToast(status ? `${msg} (HTTP ${status})` : msg, Severty.ERROR);
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    appliedFilters.q,
    appliedFilters.sourceType,
    appliedFilters.dateRange,
  ]);

  const applyFilters = () => {
    setAppliedFilters({
      ...filters,
      q: String(filters.q || "").trim(),
    });
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ q: "", sourceType: "all", dateRange: undefined });
    setAppliedFilters({ q: "", sourceType: "all", dateRange: undefined });
    setPagination({ current: 1, pageSize: 10 });
  };

  const handleView = async (record) => {
    if (!record?.id) return;
    setSelected(record);
    setViewModalOpen(true);

    await request({
      url: `${apiPath.authAdminCustomIngredientById}/${record.id}`,
      method: "GET",
      onSuccess: (data) => {
        if (data?.item) setSelected(data.item);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load details";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleEdit = async (record) => {
    if (!record?.id) return;
    setSelected(record);
    setEditModalOpen(true);
    editForm.resetFields();
    setEditLoading(true);
    await request({
      url: `${apiPath.authAdminCustomIngredientById}/${record.id}`,
      method: "GET",
      onSuccess: (data) => {
        const item = data?.item;
        if (!item) return;
        setSelected(item);
        const n = item.nutrition || {};
        manualEnergyFieldsRef.current = {
          energyKcal: !isBlank(n.energyKcal ?? n.calories),
          energyKj: !isBlank(n.energyKj),
        };
        editForm.setFieldsValue({
          name: item.name || "",
          category: item.category || "",
          description: item.description || "",
          gbpPerKg: item.gbpPerKg ?? undefined,
          weightGrams: item.weightGrams ?? undefined,
          percentInBatch: item.percentInBatch ?? undefined,
          energyKcal: n.energyKcal ?? n.calories ?? undefined,
          energyKj: n.energyKj ?? undefined,
          protein: n.protein ?? undefined,
          carbohydrates: n.carbohydrates ?? n.carbs ?? undefined,
          fat: n.fat ?? undefined,
          saturatedFat: n.saturatedFat ?? n.satFat ?? n.sat_fat ?? undefined,
          sugar: n.sugar ?? n.sugars ?? undefined,
          fibre: n.fibre ?? undefined,
          salt: n.salt ?? undefined,
          allergens: Array.isArray(item.allergens) ? item.allergens : [],
          mayContain: Array.isArray(item.mayContain) ? item.mayContain : [],
        });
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load ingredient";
        ShowToast(msg, Severty.ERROR);
        setEditModalOpen(false);
      },
    });
    setEditLoading(false);
  };

  const handleSaveEdit = async (values) => {
    if (!selected?.id) return;
    const prevNutrition =
      selected.nutrition && typeof selected.nutrition === "object"
        ? { ...selected.nutrition }
        : {};
    const nutrition = {
      ...prevNutrition,
      energyKcal: values.energyKcal ?? null,
      energyKj: values.energyKj ?? null,
      protein: values.protein ?? null,
      carbohydrates: values.carbohydrates ?? null,
      fat: values.fat ?? null,
      saturatedFat: values.saturatedFat ?? null,
      satFat: values.saturatedFat ?? null,
      sat_fat: values.saturatedFat ?? null,
      sugars: values.sugar ?? null,
      sugar: values.sugar ?? null,
      fibre: values.fibre ?? null,
      salt: values.salt ?? null,
    };
    if (nutrition.energyKcal != null) nutrition.calories = nutrition.energyKcal;

    setSavingEdit(true);
    try {
      await request({
        url: `${apiPath.authAdminCustomIngredientUpdate}/${selected.id}`,
        method: "PATCH",
        data: {
          name: values.name,
          category: values.category || null,
          description: values.description || null,
          nutrition,
          costing: { gbpPerKg: values.gbpPerKg ?? null },
          weightGrams: values.weightGrams ?? null,
          percentInBatch: values.percentInBatch ?? null,
          allergens: normalizeUk14Allergens(values.allergens),
          mayContain: normalizeUk14Allergens(values.mayContain),
        },
        onSuccess: () => {
          ShowToast("Custom ingredient updated", Severty.SUCCESS);
          setEditModalOpen(false);
          setSelected(null);
          fetchItems();
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to update ingredient";
          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record?.id) return;
    setDeletingId(record.id);
    try {
      await request({
        url: `${apiPath.authAdminCustomIngredientDelete}/${record.id}`,
        method: "DELETE",
        onSuccess: () => {
          ShowToast("Custom ingredient deleted", Severty.SUCCESS);
          if (selected?.id === record.id) {
            setSelected(null);
            setViewModalOpen(false);
            setEditModalOpen(false);
          }
          fetchItems();
        },
        onError: (err) => {
          const msg =
            err?.response?.data?.message || err?.message || "Delete failed";
          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateIngredient = async (values) => {
    const nutrition = {
      energyKcal: values.energyKcal ?? null,
      calories: values.energyKcal ?? null,
      energyKj: values.energyKj ?? null,
      protein: values.protein ?? null,
      carbohydrates: values.carbohydrates ?? null,
      fat: values.fat ?? null,
      saturatedFat: values.saturatedFat ?? null,
      satFat: values.saturatedFat ?? null,
      sat_fat: values.saturatedFat ?? null,
      sugar: values.sugar ?? null,
      sugars: values.sugar ?? null,
      fibre: values.fibre ?? null,
      salt: values.salt ?? null,
    };

    setSavingCreate(true);

    try {
      await request({
        url: apiPath.authAdminCustomIngredientCreate,
        method: "POST",
        data: {
          name: values.name,
          category: values.category || null,
          description: values.description || null,
          nutrition,
          costing: {
            gbpPerKg: values.gbpPerKg ?? null,
          },
          weightGrams: values.weightGrams ?? null,
          percentInBatch: values.percentInBatch ?? null,
          allergens: normalizeUk14Allergens(values.allergens),
          mayContain: normalizeUk14Allergens(values.mayContain),
        },

        onSuccess: () => {
          ShowToast("Custom ingredient created successfully", Severty.SUCCESS);

          setCreateModalOpen(false);
          editForm.resetFields();

          fetchItems();
        },

        onError: (err) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to create ingredient";

          ShowToast(msg, Severty.ERROR);
        },
      });
    } finally {
      setSavingCreate(false);
    }
  };

  const columns = [
    {
      title: "Ingredient",
      dataIndex: "name",
      key: "name",
      render: (v) => withTooltip(v),
    },
    {
      title: "Submitted By",
      key: "submittedBy",
      render: (_, record) => withTooltip(record?.submittedByEmail),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (v) => withTooltip(v),
    },
    {
      title: "Source",
      key: "sourceType",
      render: (_, record) => withTooltip(record?.sourceType || "custom"),
    },
    {
      title: "Submitted Date",
      key: "createdAt",
      render: (_, record) =>
        record?.createdAt ? moment(record.createdAt).format("YYYY-MM-DD") : "—",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={statusTagColor(record.status)}>
          {record.status.charAt(0).toUpperCase() + record.status.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <Space size={8}>
            <Button
              type="text"
              className="table-view-button"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleView(record);
              }}
            />

            <Button
              type="text"
              className="table-edit-button"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(record);
              }}
            />

            <Popconfirm
              title="Delete this custom ingredient?"
              okText="Delete"
              cancelText="Cancel"
              onConfirm={() => handleDelete(record)}
              okButtonProps={{
                danger: true,
                loading: deletingId === record.id,
              }}
            >
              <Button
                type="text"
                className="table-delete-button"
                icon={<DeleteOutlined />}
                disabled={deletingId === record.id}
                style={{ color: "#ff4d4f" }}
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      bordered={false}
      className="criclebox"
      title="Custom Ingredient Manager"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            editForm.resetFields();
            setSelected(null);
            setCreateModalOpen(true);
          }}
        >
          Add Ingredient
        </Button>
      }
    >
      <div
        style={{ marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap" }}
      >
        <Search
          size="large"
          placeholder="Search by ingredient name or submitted by user name/email"
          value={filters.q}
          onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
          onSearch={(value) => {
            setFilters((p) => ({ ...p, q: value }));
            setAppliedFilters((prev) => ({
              ...prev,
              q: String(value || "").trim(),
              sourceType: filters.sourceType,
              dateRange: filters.dateRange,
            }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
          style={{ width: 320 }}
          allowClear
        />

        <Select
          placeholder="Source Type"
          value={filters.sourceType}
          style={{ width: 180 }}
          onChange={(value) => {
            setFilters((p) => ({ ...p, sourceType: value }));
            setAppliedFilters((prev) => ({
              ...prev,
              q: String(filters.q || "").trim(),
              sourceType: value,
              dateRange: filters.dateRange,
            }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
        >
          <Select.Option value="all">All Sources</Select.Option>
          <Select.Option value="master">Master</Select.Option>
          <Select.Option value="custom">Custom</Select.Option>
          <Select.Option value="compound">Compound</Select.Option>
        </Select>

        <RangePicker
          value={filters.dateRange}
          onChange={(dates) => setFilters((p) => ({ ...p, dateRange: dates }))}
          placeholder={["From", "To"]}
        />

        <Button type="primary" onClick={applyFilters} loading={loading}>
          Apply Filters
        </Button>
        <Button onClick={clearFilters}>Clear</Button>
      </div>
      <div className="table-responsive">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          // scroll={{ x: 1200 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          onChange={(p) =>
            setPagination((prev) => ({
              ...prev,
              current: p.current,
              pageSize: p.pageSize,
            }))
          }
          locale={{
            emptyText: (
              <div style={{ padding: "24px 0", textAlign: "center" }}>
                No ingredients found
              </div>
            ),
          }}
        />
      </div>

      {/* View Modal */}
      <Modal
        open={viewModalOpen}
        title="Ingredient Details"
        footer={null}
        onCancel={() => {
          setViewModalOpen(false);
        }}
        destroyOnClose
      >
        {!selected ? null : (
          <>
            <div className="ingredient-details-card">
              <Typography.Title level={5} className="ingredient-details-title">
                INGREDIENT DETAILS
              </Typography.Title>
              <div className="ingredient-details-grid">
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">
                    Ingredient Name
                  </Typography.Text>
                  <div className="ingredient-details-value">
                    {selected.name || "—"}
                  </div>
                </div>
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">Category</Typography.Text>
                  <div className="ingredient-details-value">
                    {selected.category || "—"}
                  </div>
                </div>
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">
                    Submitted By Email
                  </Typography.Text>
                  <div className="ingredient-details-value">
                    {selected.submittedByEmail || "—"}
                  </div>
                </div>
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">Status</Typography.Text>
                  <div className="ingredient-details-status">
                    <Tag color={statusTagColor(selected.status)}>
                      {selected.status}
                    </Tag>
                  </div>
                </div>
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">
                    Submitted Date
                  </Typography.Text>
                  <div className="ingredient-details-value">
                    {selected.createdAt
                      ? moment(selected.createdAt).format("YYYY-MM-DD")
                      : "—"}
                  </div>
                </div>
                <div className="ingredient-details-item">
                  <Typography.Text type="secondary">
                    £ per Kg (costing)
                  </Typography.Text>
                  <div className="ingredient-details-value">
                    {selected.gbpPerKg != null && selected.gbpPerKg !== ""
                      ? `£${Number(selected.gbpPerKg).toFixed(2)}`
                      : "—"}
                  </div>
                </div>
              </div>
              <div className="ingredient-details-description">
                <Typography.Text type="secondary">Description</Typography.Text>
                <div className="ingredient-details-description-value">
                  {selected.description || "—"}
                </div>
              </div>
              {selected.status === "rejected" ? (
                <div className="ingredient-details-rejection">
                  <Typography.Text type="secondary">
                    Rejection Reason
                  </Typography.Text>
                  <div className="ingredient-details-rejection-value">
                    {selected.rejectionReason || "—"}
                  </div>
                </div>
              ) : null}
            </div>
            <div style={{ marginTop: 16 }}>
              <div className="ingredient-nutrition-card">
                <Typography.Title
                  level={5}
                  className="ingredient-details-title"
                >
                  NUTRITION PANEL
                </Typography.Title>

                <div className="ingredient-nutrition-head">
                  <div />
                  <div className="ingredient-nutrition-align-right">
                    Per 100g
                  </div>
                  <div className="ingredient-nutrition-align-right">
                    Per Portion
                  </div>
                </div>

                {(() => {
                  const n = selected.nutrition || {};
                  const portionWeight =
                    Number(selected.weightGrams) > 0
                      ? Number(selected.weightGrams)
                      : 50;
                  const factor = portionWeight / 100;
                  const rows = [
                    {
                      label: "Energy (kcal)",
                      value: n.energyKcal ?? n.calories,
                      unit: " kcal",
                      digits: 0,
                    },
                    {
                      label: "Energy (kJ)",
                      value: n.energyKj,
                      unit: " kJ",
                      digits: 0,
                    },
                    { label: "Fat", value: n.fat },
                    {
                      label: "Saturated Fat",
                      value: n.saturatedFat ?? n.saturatedFat,
                    },
                    {
                      label: "Carbohydrates",
                      value: n.carbohydrates ?? n.carbs,
                    },
                    { label: "Sugar", value: n.sugar },
                    { label: "Fibre", value: n.fibre },
                    { label: "Protein", value: n.protein },
                    { label: "Salt", value: n.salt, digits: 2 },
                  ];

                  return rows.map((row) => {
                    const per100 = toNumOrNull(row.value);
                    const perPortion = per100 == null ? null : per100 * factor;
                    const unit = row.unit || "g";
                    const digits = row.digits ?? 1;
                    return (
                      <div key={row.label} className="ingredient-nutrition-row">
                        <div>{row.label}</div>
                        <div className="ingredient-nutrition-align-right">
                          {formatNutrient(per100, unit, digits)}
                        </div>
                        <div className="ingredient-nutrition-align-right ingredient-nutrition-strong">
                          {formatNutrient(perPortion, unit, digits)}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </>
        )}
      </Modal>

     {/* Create Modal */}
      <Modal
        open={createModalOpen}
        title="Add Custom Ingredient"
        okText="Create"
        width={1000}
        className="tab-modal"
        confirmLoading={savingCreate}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => editForm.submit()}
        wrapClassName="custom-ingredient-edit-modal-square"
        destroyOnClose
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleCreateIngredient}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Ingredient Name"
                rules={[
                  { required: true, message: "Please enter ingredient name" },
                ]}
              >
                <Input placeholder="Enter ingredient name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[
                  { required: true, message: "Please select category" },
                ]}
              >
                <Select
                  options={categoryOptions}
                  loading={categoryLoading}
                  allowClear
                  showSearch
                  placeholder="Select a category"
                  filterOption={(inputValue, option) =>
                    String(option?.label || option?.value || "")
                      .toLowerCase()
                      .includes(String(inputValue || "").toLowerCase())
                  }
                  notFoundContent={
                    categoryLoading ? <Spin size="small" /> : "No categories found"
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="description" label="Description">
                <TextArea rows={3} placeholder="Enter description" />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Costing
          </Typography.Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item name="gbpPerKg" label="Unit Cost (£ per Kg)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 12.50"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="weightGrams" label="Weight (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 100"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="percentInBatch" label="Ingredient % in Batch" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  placeholder="e.g. 10"
                />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Text strong style={{ display: "block", marginBottom: 8, marginTop: 8 }}>
            Custom nutrition (optional)
          </Typography.Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item name="energyKcal" label="Energy (kcal)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 350"
                  onChange={(e) => {
                    manualEnergyFieldsRef.current.energyKcal = true;
                    const kcal = Number(e.target.value);
                    if (!Number.isFinite(kcal)) return;

                    const currentKj = editForm.getFieldValue("energyKj");
                    if (!manualEnergyFieldsRef.current.energyKj || isBlank(currentKj)) {
                      editForm.setFieldsValue({
                        energyKj: roundEnergy(kcal * 4.184),
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="energyKj" label="Energy (kJ)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 1460"
                  onChange={(e) => {
                    manualEnergyFieldsRef.current.energyKj = true;
                    const kj = Number(e.target.value);
                    if (!Number.isFinite(kj)) return;

                    const currentKcal = editForm.getFieldValue("energyKcal");
                    if (!manualEnergyFieldsRef.current.energyKcal || isBlank(currentKcal)) {
                      editForm.setFieldsValue({
                        energyKcal: roundEnergy(kj / 4.184),
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="protein" label="Protein (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 12.5"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item name="carbohydrates" label="Carbohydrates (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 68.0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="fat" label="Fat (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 5.2"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="saturatedFat" label="Saturated Fat (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 1.1"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Form.Item name="sugar" label="Sugars (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 15.0"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="fibre" label="Fibre (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 8.5"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="salt" label="Salt (g)" rules={[nonNegativeRule]}>
                <Input
                  className="w-100"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 0.05"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item name="allergens" label="Allergens">
                <Select
                  mode="multiple"
                  allowClear
                  showArrow
                  placeholder="Select allergens"
                  options={allergenOptionsForContains}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="mayContain" label="May Contain">
                <Select
                  mode="multiple"
                  allowClear
                  showArrow
                  placeholder="Select may contain allergens"
                  options={allergenOptionsForMayContain}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModalOpen}
        title="Edit Custom Ingredient"
        okText="Save"
        width={1000}
        className="tab-modal"
        confirmLoading={savingEdit}
        okButtonProps={{ disabled: editLoading }}
        onCancel={() => setEditModalOpen(false)}
        onOk={() => editForm.submit()}
        wrapClassName="custom-ingredient-edit-modal-square"
        destroyOnClose
      >
        <Spin spinning={editLoading}>
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleSaveEdit}
            disabled={editLoading}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Ingredient Name"
                  rules={[
                    { required: true, message: "Please enter ingredient name" },
                  ]}
                >
                  <Input placeholder="Enter ingredient name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[
                    { required: true, message: "Please enter or select category" },
                    {
                      validator: (_, value) => {
                        if (typeof value === "string" && !value.trim()) {
                          return Promise.reject(
                            new Error("Category cannot be empty"),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <AutoComplete
                    options={categoryOptions}
                    loading={categoryLoading}
                    allowClear
                    showSearch
                    filterOption={(inputValue, option) =>
                      String(option?.value || "")
                        .toLowerCase()
                        .includes(String(inputValue || "").toLowerCase())
                    }
                    onChange={(val) => {
                      editForm.setFieldsValue({ category: val });
                    }}
                    onSelect={(val) => {
                      editForm.setFieldsValue({ category: val });
                    }}
                  >
                    <Input placeholder="Type or select a category" />
                  </AutoComplete>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item name="description" label="Description">
                  <TextArea rows={3} placeholder="Enter description" />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
              Costing
            </Typography.Text>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item name="gbpPerKg" label="Unit Cost (£ per Kg)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 12.50"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="weightGrams" label="Weight (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 100"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="percentInBatch" label="Ingredient % in Batch" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    placeholder="e.g. 10"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Typography.Text strong style={{ display: "block", marginBottom: 8, marginTop: 8 }}>
              Custom nutrition (optional)
            </Typography.Text>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item name="energyKcal" label="Energy (kcal)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 350"
                    onChange={(e) => {
                      manualEnergyFieldsRef.current.energyKcal = true;
                      const kcal = Number(e.target.value);
                      if (!Number.isFinite(kcal)) return;

                      const currentKj = editForm.getFieldValue("energyKj");
                      if (!manualEnergyFieldsRef.current.energyKj || isBlank(currentKj)) {
                        editForm.setFieldsValue({
                          energyKj: roundEnergy(kcal * 4.184),
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="energyKj" label="Energy (kJ)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 1460"
                    onChange={(e) => {
                      manualEnergyFieldsRef.current.energyKj = true;
                      const kj = Number(e.target.value);
                      if (!Number.isFinite(kj)) return;

                      const currentKcal = editForm.getFieldValue("energyKcal");
                      if (!manualEnergyFieldsRef.current.energyKcal || isBlank(currentKcal)) {
                        editForm.setFieldsValue({
                          energyKcal: roundEnergy(kj / 4.184),
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="protein" label="Protein (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 12.5"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item name="carbohydrates" label="Carbohydrates (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 68.0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="fat" label="Fat (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 5.2"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="saturatedFat" label="Saturated Fat (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 1.1"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Form.Item name="sugar" label="Sugars (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 15.0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="fibre" label="Fibre (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 8.5"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="salt" label="Salt (g)" rules={[nonNegativeRule]}>
                  <Input
                    className="w-100"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="e.g. 0.05"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item name="allergens" label="Allergens">
                  <Select
                    mode="multiple"
                    allowClear
                    showArrow
                    placeholder="Select allergens"
                    options={allergenOptionsForContains}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="mayContain" label="May Contain">
                  <Select
                    mode="multiple"
                    allowClear
                    showArrow
                    placeholder="Select may contain allergens"
                    options={allergenOptionsForMayContain}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
};

export default CustomIngredientManager;
